import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Users, Loader2 } from "lucide-react";
import { Footer } from "@/components/landing/FooterCTA";
import { PlayerCard } from "@/components/plantel/PlayerCard";
import { Navbar } from "@/components/landing/Navbar";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import logo from "../assets/logo.png";

export default function RosterPage() {
  const { slug } = useParams();
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/plantel/rosters/by-slug/${slug}`)
      .then(({ data }) => setRoster(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const players = roster?.players || [];

  return (
    <div className="min-h-screen bg-[#eef1f7] flex flex-col">
      <Navbar />
      <div className="pt-16 sm:pt-20">
        <Breadcrumbs items={[
          { label: "Plantel", to: "/plantel" },
          { label: roster ? `${roster.category}${roster.subcategory ? ` · ${roster.subcategory}` : ""}` : "Plantel" },
        ]} />
      </div>

      {loading ? (
        <div className="flex-1 py-24 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
      ) : notFound || !roster ? (
        <div className="flex-1 py-24 text-center text-gray-400">
          <Users className="h-10 w-10 mx-auto" />
          <p className="mt-3">No encontramos este plantel.</p>
          <Link to="/plantel" className="mt-4 inline-block text-sm font-semibold text-navy underline">Ver todos los planteles</Link>
        </div>
      ) : (
        <>
          <section className="bg-navy text-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-10 pt-4">
              <span className="section-tag section-tag--dark">Temporada {roster.season}</span>
              <h1 data-testid="roster-title" className="mt-4 text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
                {roster.category}{roster.subcategory ? ` · ${roster.subcategory}` : ""}
              </h1>
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-white/70">
                <Users className="h-4 w-4 text-gold" /> {players.length} jugador{players.length === 1 ? "" : "es"}
              </div>
              {roster.description && <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl">{roster.description}</p>}
            </div>
          </section>

          <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-8">
            {players.length === 0 ? (
              <p className="py-16 text-center text-sm text-gray-400">Este plantel todavía no tiene jugadores.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {players.map((p, i) => <PlayerCard key={p.id} player={p} i={i} />)}
              </div>
            )}
          </main>
        </>
      )}

      <Footer />
    </div>
  );
}
